import {
    Observable
} from "rxjs";
import {
    map
} from "rxjs/operators";

import {
    Injectable,
    inject
} from "@angular/core";
import {
    BulkResponse,
    BulkUtil,
    CreateRequest,
    DeleteRequest,
    DeleteResponse,
    GetRequest,
    Realm,
    RequestUtil,
    SearchResponse,
    Team,
    TeamSearchFilterOptions,
    UpdateRequest
} from "@shopliftr/common-js/shared";
import {
    ISearchSort
} from "@shopliftr/common-js/data";
import { ModelAction } from "@shopliftr/common-js/data";
import { IFullAccessInternalApiClientService } from "../../../../interfaces/internal-api-client";
import { InternalApiRequestService } from "../../request/internal-api/internal-api-request.service";
import { ListUtil } from "../../../../list/util/list-util";

/**
 * Team service for all the crud operations related to Team model.
 * Implements IFullAccessInternalApiClientService providing full CRUD access including
 * bulk operations for the Team model via the Internal API v2.
 *
 * @pattern IFullAccessInternalApiClientService
 * @providedIn root
 */
@Injectable({
    providedIn: "root"
})
export class TeamApiClientService implements IFullAccessInternalApiClientService<Team> {

    readonly basePath = "team";

    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    /**
     * Search for teams for the given realms and sorted by name
     */
    getTeams$(specificRealms?: Array<Realm>, sort?: Array<ISearchSort>): Observable<Array<Team>> {

        const teamSearchFilterOptions = new TeamSearchFilterOptions().setDefaults();
        teamSearchFilterOptions.realms = specificRealms;

        return this.getAll$(teamSearchFilterOptions, sort);
    }

    /**
     * Search for a team with given search filter options
     * @see InternalApiRequestService.search
     */
    search$(
        filterOptions: TeamSearchFilterOptions,
        page?: number,
        pageSize?: number,
        sort?: Array<ISearchSort>
    ): Observable<SearchResponse<Team>> {

        const searchRequest = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            Team.modelName,
            ListUtil.getFromValue(page, pageSize),
            pageSize,
            filterOptions,
            sort
        );

        return this._internalApiRequestService.search$(searchRequest, this.basePath);
    }

    /**
     * Get all the team with a given search filter options
     * Append all the paginated results to response
     * @see InternalApiRequestService.getAll
     */
    getAll$(filterOptions: TeamSearchFilterOptions, sort?: Array<ISearchSort>): Observable<Array<Team>> {

        const searchRequest = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            Team.modelName,
            0,
            100,
            filterOptions,
            sort
        );

        return this._internalApiRequestService.getAll$(searchRequest, this.basePath);
    }

    /**
     * Get a team use internal api request service
     * @see InternalApiRequestService.get
     */
    get$(id: string): Observable<Team> {

        const getRequest: GetRequest<Team> = RequestUtil.createGetRequest(
            id, Team.modelName, ModelAction.ClientGet
        );
        return this._internalApiRequestService.get$(getRequest, this.basePath).pipe(map((response) => response.payload as Team));
    }

    /**
     * Bulk get teams by their ids
     */
    bulkGet$(ids: Array<string>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(ids.map((id: string) => {
            const request = RequestUtil.createGetRequest<Team>(id, Team.modelName, ModelAction.ClientGet);
            return BulkUtil.createRequestItem(request);
        }));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Create a team use internal api request service
     * @see InternalApiRequestService.create
     */
    create$(fields: Team): Observable<Team> {

        const createRequest = RequestUtil.createCreateRequest<Team, void>(
            ModelAction.ClientCreate,
            fields.cloneForAction(ModelAction.ClientCreate)
        );
        return this._internalApiRequestService.create$(createRequest, this.basePath).pipe(map((response) => response.payload));
    }

    /**
     * Bulk create teams
     */
    bulkCreate$(items: Array<CreateRequest<Team, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            items.map((item) => BulkUtil.createRequestItem(item))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Update a team use internal api request service
     * @see InternalApiRequestService.update
     */
    update$(id: string, version: number, fields: Team, removeFields?: Array<string>): Observable<Team> {

        const updateFields = fields.cloneForAction(ModelAction.ClientUpdate);
        const updateRequest: UpdateRequest<Team> = RequestUtil.createUpdateRequest(
            id, ModelAction.ClientUpdate, updateFields, version, removeFields
        );
        return this._internalApiRequestService.update$(updateRequest, this.basePath).pipe(map((response) => response.payload));
    }

    /**
     * Update a team with changes computed from original and updated models
     */
    updateWithChanges$(original: Team, updated: Team): Observable<Team> {
        return this._internalApiRequestService.updateWithChanges$(original, updated, this.basePath);
    }

    /**
     * Bulk update teams
     */
    bulkUpdate$(requests: Array<UpdateRequest<unknown, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            requests.map((request) => BulkUtil.createRequestItem(request))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Delete a team by id and version
     */
    delete$(id: string, version: number): Observable<DeleteResponse> {
        const deleteRequest = RequestUtil.createDeleteRequest<Team>(
            id, Team.modelName, ModelAction.ClientDelete, version
        );
        return this._internalApiRequestService.delete$(deleteRequest, this.basePath);
    }

    /**
     * Bulk delete teams
     */
    bulkDelete$(items: Array<DeleteRequest<unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            items.map((item) => BulkUtil.createRequestItem(item))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }
}
